/*
 * Renderers for the free-design widgets (docs/hmi-panel-ausbau.md §6):
 * action-button, text, hero-value, image, shape, symbol, nav-bar.
 *
 * They size themselves from their box (container queries), because in a
 * free layout the author sets the box and expects the content to fill it:
 * a hero value in a 400 px box shows a bigger number than in 200 px.
 */

import { roundedRoute, validRoute } from './route';
import { useEffect, useRef, useState } from 'preact/hooks';
import type {
  ActionButtonWidget, HeroValueWidget, ImageWidget, NavBarWidget, ShapeWidget, ThemeToggleWidget, AlarmListWidget, SymbolWidget, TextWidget,
} from '../widgets';
import type { IPointBindingSource } from '../binding';
import { pointSignal } from '../pointSignal';
import { isDegraded } from '../quality';
import { useLeafMeta, useLeafMetaLookup } from '../LeafMetaContext';
import { enumLabelFor, formatLeafValue } from '../../format/leafValue';
import { HmiScriptError, compile, evaluateCondition, run, type ScriptContext } from '../script/hmiScript';
import { isHexColor } from '../themeModel';
import { drainWrite, useNamedPoints } from './namedPoints';
import { useHmiRuntime } from './runtimeContext';
import { publishFollow } from './followStore';
import { Icon, ICONS, SYMBOLS } from './symbols';
import './panel.css';

interface P<W> { widget: W; source: IPointBindingSource; }

// ------------------------------------------------------------ action button

type BtnState = 'idle' | 'armed' | 'busy' | 'ok' | 'error';

/** How long a value we just wrote counts as current, before the next live
 *  value has arrived. Without it, holding "+" would add 0.5 to the same
 *  old value five times. */
const OPTIMISTIC_MS = 4000;

export function ActionButtonRenderer({ widget, source }: P<ActionButtonWidget>) {
  const runtime = useHmiRuntime();
  const named = useNamedPoints(widget.points, source);
  const [state, setState] = useState<BtnState>('idle');
  const [message, setMessage] = useState<string | null>(null);
  const pending = useRef(new Map<string, { value: unknown; at: number }>());
  const repeat = useRef<number | null>(null);
  const abort = useRef<AbortController | null>(null);
  const armTimer = useRef<number | null>(null);

  useEffect(() => () => {
    stopRepeat();
    abort.current?.abort();
    if (armTimer.current) window.clearTimeout(armTimer.current);
  }, []);

  const ctx: ScriptContext = {
    pointNames: named.ctx.pointNames,
    value: (n) => {
      const p = pending.current.get(n);
      if (p && Date.now() - p.at < OPTIMISTIC_MS) return p.value;
      return named.values[n];
    },
  };

  async function fire(): Promise<void> {
    if (runtime.editing) return;
    abort.current ??= new AbortController();
    const signal = abort.current.signal;
    try {
      const writes: Array<{ point: string; value: unknown }> = [];
      let target: string | null = null;
      for (const action of widget.actions) {
        if (action.type === 'navigate' && action.screenId) target = action.screenId;
        if (action.type === 'write' && action.point) writes.push({ point: action.point, value: action.value });
        if (action.type === 'script' && action.script) {
          const out = run(compile(action.script), ctx);
          writes.push(...out.writes);
          if (out.navigate) target = out.navigate;
        }
      }
      for (const w of writes) pending.current.set(w.point, { value: w.value, at: Date.now() });
      if (target) runtime.navigate(target);
      if (writes.length === 0) return;
      setState('busy');
      const results = await Promise.all(writes.map((w) => {
        const key = named.binding(w.point);
        return key ? drainWrite(source, key, w.value, signal) : Promise.resolve(false);
      }));
      const ok = results.every(Boolean);
      setState(ok ? 'ok' : 'error');
      setMessage(ok ? null : 'Der Regler hat den Wert nicht übernommen.');
      window.setTimeout(() => setState((s) => (s === 'ok' || s === 'error' ? 'idle' : s)), ok ? 600 : 2500);
    } catch (e) {
      if (e instanceof DOMException && e.name === 'AbortError') return;
      stopRepeat();
      setState('error');
      setMessage(e instanceof HmiScriptError ? `Skript: ${e.message}` : String(e));
      window.setTimeout(() => setState('idle'), 3000);
    }
  }

  function stopRepeat(): void {
    if (repeat.current !== null) {
      window.clearInterval(repeat.current);
      repeat.current = null;
    }
  }

  function onDown(e: PointerEvent): void {
    if (runtime.editing) return;
    (e.currentTarget as HTMLElement).setPointerCapture?.(e.pointerId);
    if (widget.confirm && state !== 'armed') {
      setState('armed');
      if (armTimer.current) window.clearTimeout(armTimer.current);
      armTimer.current = window.setTimeout(() => setState((s) => (s === 'armed' ? 'idle' : s)), 3000);
      return;
    }
    void fire();
    if (widget.repeatMs && widget.repeatMs >= 150 && !widget.confirm) {
      stopRepeat();
      // First repeat after a pause, like a keyboard, so a tap stays one step.
      repeat.current = window.setTimeout(() => {
        repeat.current = window.setInterval(() => void fire(), widget.repeatMs!);
      }, 450) as unknown as number;
    }
  }

  const shape = widget.shape ?? 'rounded';
  const label = state === 'armed' ? (widget.confirm || 'Sicher?') : widget.text;
  const iconOnly = !label && widget.icon;
  return (
    <button
      type="button"
      class={`hv-abtn hv-abtn--${shape} hv-abtn--${state}${iconOnly ? ' hv-abtn--icon' : ''}`}
      title={message ?? widget.label ?? widget.text ?? ''}
      aria-label={widget.label ?? widget.text ?? widget.icon ?? 'Knopf'}
      onPointerDown={onDown}
      onPointerUp={stopRepeat}
      onPointerCancel={stopRepeat}
      onPointerLeave={stopRepeat}
    >
      {widget.icon && ICONS[widget.icon] && <span class="hv-abtn__icon"><Icon name={widget.icon} size="100%" /></span>}
      {label && <span class="hv-abtn__text">{label}</span>}
    </button>
  );
}

// ------------------------------------------------------------ text

function useTemplate(content: string, points: Record<string, string> | undefined, source: IPointBindingSource): string {
  const named = useNamedPoints(points, source);
  const lookup = useLeafMetaLookup();
  return content.replace(/\{([A-Za-z_][A-Za-z0-9_]*)\}/g, (all, name: string) => {
    if (!(name in named.values)) return all;
    const key = named.binding(name);
    const meta = lookup?.(key);
    const raw = named.values[name];
    if (isNoSensor(raw)) return '–';
    const text = formatLeafValue(raw, meta);
    const numeric = typeof raw === 'number' && enumLabelFor(meta, raw) === null;
    return numeric && meta?.unit ? `${text} ${meta.unit}` : text;
  });
}

export function TextRenderer({ widget, source }: P<TextWidget>) {
  const text = useTemplate(widget.content, widget.points, source);
  const style: Record<string, string> = {};
  if (widget.size) style['--text-size'] = `${Math.min(Math.max(widget.size, 6), 400)}px`;
  return (
    <div class={`hv-ptext hv-ptext--${widget.weight ?? 'regular'}${widget.uppercase ? ' hv-ptext--upper' : ''}`} style={style}>
      {text}
    </div>
  );
}

// ------------------------------------------------------------ hero value

/** Controllers mark a missing sensor with a huge negative number (Carel:
 *  -999.9 or -9999). No real reading in a building comes near it. */
export function isNoSensor(raw: unknown): boolean {
  return typeof raw === 'number' && raw <= -999;
}

function splitNumber(text: string): { int: string; frac: string } {
  const m = /^(-?[\d.]+)(,\d+)?$/.exec(text);
  return m ? { int: m[1]!, frac: m[2] ?? '' } : { int: text, frac: '' };
}

function SideValue({ s, source }: { s: NonNullable<HeroValueWidget['sideValues']>[number]; source: IPointBindingSource }) {
  const { value, release } = pointSignal(source, s.point);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(s.point);
  const raw = value.value?.value ?? null;
  const unit = s.unit ?? meta?.unit ?? '';
  const numeric = !isNoSensor(raw) && typeof raw === 'number' && enumLabelFor(meta, raw) === null;
  return (
    <span class="hv-hero__side">
      <span class="hv-hero__side-label">{s.label}</span>
      <strong>{isNoSensor(raw) ? '–' : formatLeafValue(raw, meta, s.decimals)}{numeric ? unit : ''}</strong>
    </span>
  );
}

type HeroEntry = Pick<HeroValueWidget, 'valuePoint' | 'caption' | 'unit' | 'decimals' | 'sideValues' | 'links'>;

/**
 * Großer Wert mit Nebenwerten. Mit <c>alternates</c> zeigt die Kachel mehrere
 * Werte nacheinander, etwa die Raumtemperaturen von Heizkreis 1 bis 3
 * (Kutiak, 18.10.2026); Antippen schaltet sofort weiter.
 */
export function HeroValueRenderer({ widget, source }: P<HeroValueWidget>) {
  const alternates = widget.alternates?.filter((a) => a.valuePoint) ?? [];
  const entries: HeroEntry[] = alternates.length === 0
    ? [widget]
    : [{ ...widget, caption: widget.cycleCaption ?? widget.caption }, ...alternates];
  const [index, setIndex] = useState(0);
  const seconds = widget.cycleSeconds ?? 6;
  useEffect(() => {
    if (entries.length < 2 || seconds <= 0) return undefined;
    const id = window.setInterval(() => setIndex((i) => (i + 1) % entries.length), seconds * 1000);
    return () => window.clearInterval(id);
  }, [entries.length, seconds, index]);
  const at = index % entries.length;
  const entry = entries[at]!;
  const cycling = entries.length > 1;
  // Widgets mit follow auf diese Kachel (Plus/Minus) nehmen die Punkte des
  // gerade gezeigten Werts.
  useEffect(() => { publishFollow(widget.id, entry.links); }, [widget.id, entry]);
  return (
    <div class={`hv-hero-cycle${cycling ? ' hv-hero-cycle--on' : ''}`}
         onClick={cycling ? () => setIndex((i) => (i + 1) % entries.length) : undefined}>
      <HeroBody key={at} entry={entry} fallbackCaption={widget.label} source={source} />
      {cycling && (
        <span class="hv-hero__dots" aria-hidden="true">
          {entries.map((_, i) => <span key={i} class={i === at ? 'is-on' : ''} />)}
        </span>
      )}
    </div>
  );
}

function HeroBody({ entry, fallbackCaption, source }: { entry: HeroEntry; fallbackCaption?: string; source: IPointBindingSource }) {
  const { value, release } = pointSignal(source, entry.valuePoint);
  useEffect(() => release, [release]);
  const meta = useLeafMeta(entry.valuePoint);
  const current = value.value;
  const raw = current?.value ?? null;
  const noSensor = isNoSensor(raw);
  const text = noSensor ? 'kein Fühler' : formatLeafValue(raw, meta, entry.decimals ?? meta?.decimals ?? 1);
  const numeric = !noSensor && typeof raw === 'number' && enumLabelFor(meta, raw) === null;
  const unit = numeric ? (entry.unit ?? meta?.unit ?? '') : '';
  const degree = unit === '°' || unit === '°C';
  const { int, frac } = numeric ? splitNumber(text) : { int: text, frac: '' };
  const caption = entry.caption ?? fallbackCaption;
  return (
    <div class={`hv-hero${isDegraded(current) ? ' hv-hero--stale' : ''}${noSensor ? ' hv-hero--none' : ''}`}>
      {caption && <span class="hv-hero__caption">{caption}</span>}
      <span class="hv-hero__value">
        <span class="hv-hero__int">{int}</span>
        {frac && <span class="hv-hero__frac">{frac}</span>}
        {unit && <span class={degree ? 'hv-hero__deg' : 'hv-hero__unit'}>{degree ? '°' : unit}</span>}
      </span>
      {entry.sideValues && entry.sideValues.length > 0 && (
        <span class="hv-hero__sides">
          {entry.sideValues.map((s, i) => <SideValue key={i} s={s} source={source} />)}
        </span>
      )}
    </div>
  );
}

// ------------------------------------------------------------ image

/** Markengrafiken, die Laufzeit und App selbst mitbringen (public/brand),
 *  statt sie je Instanz hochzuladen: "brand:austriaemail/logo" nimmt je nach
 *  Hell/Dunkel logo-on-dark.svg oder logo-on-light.svg. So kann ein
 *  Erkennungsset das Logo setzen, ohne eine Kennung aus einer Datenbank. */
export function brandAssetUrl(ref: string, scheme: 'light' | 'dark'): string | null {
  const m = /^brand:([a-z0-9-]+)\/([a-z0-9-]+)$/.exec(ref);
  if (!m) return null;
  const file = m[2] === 'logo' ? `logo-on-${scheme === 'light' ? 'light' : 'dark'}.svg` : `${m[2]}.png`;
  return `${import.meta.env.BASE_URL}brand/${m[1]}/${file}`;
}

export function ImageRenderer({ widget }: P<ImageWidget>) {
  const runtime = useHmiRuntime();
  const [failed, setFailed] = useState(false);
  if (failed) return <div class="hv-pimage hv-pimage--missing">Bild fehlt</div>;
  return (
    <img
      class="hv-pimage"
      src={brandAssetUrl(widget.assetRef, runtime.scheme) ?? runtime.assetUrl(widget.assetRef)}
      alt={widget.label ?? ''}
      draggable={false}
      style={{ objectFit: widget.fit ?? 'contain', opacity: String(widget.opacity ?? 1) }}
      onError={() => setFailed(true)}
    />
  );
}

// ------------------------------------------------------------ shape

export function ShapeRenderer({ widget, source }: P<ShapeWidget>) {
  const named = useNamedPoints(widget.points, source);
  const flowing = widget.shape === 'pipe' && evaluateCondition(widget.flowWhen, named.ctx);
  const fill = isHexColor(widget.fill) ? widget.fill : 'none';
  const stroke = isHexColor(widget.stroke) ? widget.stroke : 'var(--accent)';
  const sw = Math.min(Math.max(widget.strokeWidth ?? 2, 0), 60);
  const vertical = widget.orientation === 'vertical';

  if (widget.shape === 'rect' || widget.shape === 'ellipse') {
    return (
      <svg class="hv-shape" viewBox="0 0 100 100" preserveAspectRatio="none">
        {widget.shape === 'rect'
          ? <rect x="0" y="0" width="100" height="100" fill={fill} stroke={stroke} stroke-width={sw} vector-effect="non-scaling-stroke" />
          : <ellipse cx="50" cy="50" rx="50" ry="50" fill={fill} stroke={stroke} stroke-width={sw} vector-effect="non-scaling-stroke" />}
      </svg>
    );
  }
  // In Pixeln gezeichnet, nicht in einer gestreckten viewBox: sonst haengen
  // Laenge und Tempo der Laufpunkte an der Groesse des Stuecks, und Leitungen
  // aus mehreren Stuecken laufen ungleich und abgehackt (EWP, 28.10.2026).
  const cls = `hv-shape hv-shape--line${flowing ? ' hv-shape--flowing' : ''}${widget.flowReverse ? ' hv-shape--reverse' : ''}`;
  const route = validRoute(widget.route);
  if (route) {
    // Verlauf mit runden Ecken: ein Pfad, damit die Laufpunkte ohne Bruch
    // um die Boegen laufen.
    const d = roundedRoute(route, Math.max(widget.cornerRadius ?? 0, 0));
    return (
      <svg class={cls} width="100%" height="100%">
        <path d={d} stroke={stroke} stroke-width={sw} fill="none" stroke-linecap="round" stroke-linejoin="round"
              opacity={widget.shape === 'pipe' ? 0.35 : 1} />
        {widget.shape === 'pipe' && (
          <path class="hv-shape__flow" d={d} stroke={stroke} stroke-width={Math.max(sw * 0.45, 2)} fill="none"
                stroke-linecap="round" stroke-linejoin="round" />
        )}
      </svg>
    );
  }
  const ends = vertical
    ? { x1: '50%', y1: '0', x2: '50%', y2: '100%' }
    : { x1: '0', y1: '50%', x2: '100%', y2: '50%' };
  return (
    <svg class={cls} width="100%" height="100%">
      <line {...ends} stroke={stroke} stroke-width={sw} stroke-linecap="round"
            opacity={widget.shape === 'pipe' ? 0.35 : 1} />
      {widget.shape === 'pipe' && (
        <line class="hv-shape__flow" {...ends} stroke={stroke} stroke-width={Math.max(sw * 0.45, 2)}
              stroke-linecap="round" />
      )}
    </svg>
  );
}

// ------------------------------------------------------------ symbol

export function SymbolRenderer({ widget, source }: P<SymbolWidget>) {
  const named = useNamedPoints(widget.points, source);
  const running = evaluateCondition(widget.runningWhen, named.ctx);
  const alarm = evaluateCondition(widget.alarmWhen, named.ctx);
  const sym = SYMBOLS[widget.symbol];
  const color = alarm ? 'var(--status-error)' : running ? (isHexColor(widget.color) ? widget.color : 'var(--accent)') : 'var(--text-secondary)';
  return (
    <div class={`hv-sym${running ? ' hv-sym--running' : ''}${alarm ? ' hv-sym--alarm' : ''}`} style={{ color }}
         title={sym?.label ?? widget.symbol}>
      <svg viewBox="0 0 64 64" aria-hidden="true">{sym ? sym.draw() : <text x="4" y="36">?</text>}</svg>
      {widget.label && <span class="hv-sym__label">{widget.label}</span>}
    </div>
  );
}

// ------------------------------------------------------------ nav bar

export function NavBarRenderer({ widget }: P<NavBarWidget>) {
  const runtime = useHmiRuntime();
  const ids = widget.screenIds && widget.screenIds.length > 0 ? widget.screenIds : runtime.screens.map((s) => s.id);
  const items = ids.map((id) => runtime.screens.find((s) => s.id === id) ?? { id, label: id }).slice(0, 8);
  return (
    <nav class="hv-navbar">
      {items.map((s) => {
        const active = s.id === runtime.currentScreenId;
        return (
          <button type="button" key={s.id} class={`hv-navbar__item${active ? ' hv-navbar__item--active' : ''}`}
                  aria-current={active ? 'page' : undefined}
                  onClick={() => !runtime.editing && runtime.navigate(s.id)}>
            {widget.showIcons !== false && s.iconRef && ICONS[s.iconRef] && <Icon name={s.iconRef} size={22} />}
            {widget.showLabels !== false && <span>{s.label}</span>}
          </button>
        );
      })}
    </nav>
  );
}

// ------------------------------------------------------------ theme toggle

export function ThemeToggleRenderer(_: P<ThemeToggleWidget>) {
  const runtime = useHmiRuntime();
  const dark = runtime.scheme === 'dark';
  const label = dark ? 'Hell anzeigen' : 'Dunkel anzeigen';
  return (
    <button type="button" class="hv-themetoggle" aria-label={label} title={label}
            disabled={!runtime.setScheme && !runtime.editing}
            onClick={() => !runtime.editing && runtime.setScheme?.(dark ? 'light' : 'dark')}>
      <Icon name={dark ? 'sun' : 'moon'} size="55%" />
    </button>
  );
}

// ------------------------------------------------------------ alarm list

export function AlarmListRenderer({ widget, source }: P<AlarmListWidget>) {
  const named = useNamedPoints(widget.points, source);
  const active = widget.entries.filter((e) => {
    const v = named.values[e.point];
    return v === true || v === 1 || v === '1';
  });
  // Solange kein Wert da ist, weiss die Liste nichts; dann nicht "keine" sagen.
  const known = widget.entries.some((e) => named.values[e.point] !== undefined);
  return (
    <div class="hv-alarmlist">
      {widget.title && (
        <div class="hv-alarmlist__head">
          <span>{widget.title}</span>
          {active.length > 0 && <span class="hv-alarmlist__count">{active.length}</span>}
        </div>
      )}
      {active.length > 0 ? (
        <ul class="hv-alarmlist__items">
          {active.map((e) => (
            <li key={e.point} class="hv-alarmlist__item"><Icon name="alarm" size={20} /><span>{e.label}</span></li>
          ))}
        </ul>
      ) : (
        <div class="hv-alarmlist__empty">
          {known ? <><Icon name="check" size={20} /><span>{widget.emptyText || 'Keine Meldungen'}</span></> : <span>…</span>}
        </div>
      )}
    </div>
  );
}
